import { STORAGE_KEYS } from "src/constants/common";
import environment from "../constants/environment";
import storage from "./storage";

export interface ChatUrlParams {
  subscriberId: string;
  subscriberName: string;
  token: string;
  clientSource?: string;
}

/**
 * Builds the chat URL with patient information and authentication
 * @param patientData - Patient data containing ID and name
 * @param clientSource - Optional client source identifier (defaults to "GoodFlipCare")
 * @returns Complete chat URL with query parameters
 */
export const buildChatUrl = (
  patientData: any,
  clientSource: string = "GoodFlipCare"
): string => {
  const token = storage.getItem(STORAGE_KEYS.AUTH_TOKEN);

  if (!patientData || !token) {
    throw new Error("Patient data or token not available");
  }

  const baseUrl = environment.CHAT_URL;
  if (!baseUrl) {
    throw new Error("Chat URL not configured in environment");
  }

  // Build the JSON object that matches your desired format
  const data = {
    uuid: "",
    moduleName: "chat",
    environment: environment.ENV || "uat",
    clientSource,
    userMetaInfo: {
      name: patientData.name,
    },
    mobileNumber: patientData?.contact_no || "",
    baseUrl,
    showChatHeader: false,
    showChatBot: false,
    token, // include if needed
  };

  // Encode JSON into query param
  const encodedData = encodeURIComponent(JSON.stringify(data));

  return `${baseUrl}?data=${encodedData}`;
};

/**
 * Opens chat in a new window with proper dimensions and settings
 * @param patientData - Patient data containing ID and name
 * @param clientSource - Optional client source identifier
 */
export const openChatWindow = (
  patientData: any,
  clientSource: string = "GoodFlipCare"
): void => {
  try {
    const chatUrl = buildChatUrl(patientData, clientSource);

    // Open chat in the same window/frame instead of new tab
    window.location.href = chatUrl;
  } catch (error) {
    console.error("Failed to open chat window:", error);
  }
};

/**
 * Gets the chat URL for iframe embedding
 * @param patientData - Patient data containing ID and name
 * @param clientSource - Optional client source identifier
 * @returns Chat URL string
 */
export const getChatUrl = (
  patientData: any,
  clientSource: string = "GoodFlipCare"
): string => {
  try {
    return buildChatUrl(patientData, clientSource);
  } catch (error) {
    console.error("Failed to build chat URL:", error);
    return "";
  }
};
